import { useState, useEffect, useRef } from "react";
import { Logo } from "@/components/Logo";
import { BookOpen, Code2, Save, Loader2, Upload, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { adminUpload } from "@/lib/adminAuth";
import { AdminLoginGate } from "@/components/AdminLoginGate";
import type { Documentation } from "@shared/schema";
import { getDocumentation, upsertDocumentation } from "@/lib/blogFirestore";

const DOC_TYPES = [
  { type: "user-documentation", label: "User Documentation", icon: BookOpen },
  { type: "developer-documentation", label: "Developer Documentation", icon: Code2 },
];

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function DocEditor({ type }: { type: string }) {
  const { toast } = useToast();
  const [doc, setDoc] = useState<Documentation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [contentTab, setContentTab] = useState<"write" | "preview">("write");
  const [form, setForm] = useState({ title: "", content: "" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    setContentTab("write");
    getDocumentation(type)
      .then((d) => {
        if (d) {
          setDoc(d);
          setForm({ title: d.title, content: d.content });
        } else {
          setDoc(null);
          setForm({ title: "", content: "" });
        }
      })
      .catch(() => {
        setDoc(null);
        setForm({ title: "", content: "" });
      })
      .finally(() => setLoading(false));
  }, [type]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      const updated = await upsertDocumentation(type, form);
      setDoc(updated);
      toast({ title: "Documentation saved!" });
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await adminUpload(formData);
      if (!res.ok) throw new Error((await res.json()).message);
      const { url } = await res.json();
      const tag = `<img src="${url}" alt="image" />`;
      setForm((f) => ({ ...f, content: f.content + "\n" + tag }));
      toast({ title: "Image uploaded", description: "Image tag inserted at end of content." });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#8B2121]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {doc?.updatedAt && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
          <Clock className="h-3.5 w-3.5" />
          Last saved {formatDate(doc.updatedAt)}
        </div>
      )}

      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Title</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          data-testid={`input-doc-title-${type}`}
          placeholder="Documentation title..."
          className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Content (HTML)</label>
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
            <button
              type="button"
              onClick={() => setContentTab("write")}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                contentTab === "write"
                  ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              Write
            </button>
            <button
              type="button"
              onClick={() => setContentTab("preview")}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                contentTab === "preview"
                  ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              Preview
            </button>
          </div>
        </div>

        {contentTab === "write" ? (
          <textarea
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            data-testid={`input-doc-content-${type}`}
            rows={18}
            placeholder="<h2>Getting Started</h2><p>Write your documentation in HTML format...</p>"
            className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-mono text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 resize-y"
          />
        ) : (
          <div className="w-full min-h-[300px] px-5 py-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            {form.content ? (
              <div
                className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-[#8B2121] prose-img:rounded-2xl"
                dangerouslySetInnerHTML={{ __html: form.content }}
              />
            ) : (
              <p className="text-slate-400 text-sm italic">Nothing to preview — write HTML in the Write tab.</p>
            )}
          </div>
        )}
      </div>

      <div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`button-upload-image-${type}`}
            className="flex items-center gap-2 px-4 py-2 rounded-2xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-500 dark:text-slate-400 hover:border-[#8B2121]/40 hover:text-[#8B2121] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload Image"}
          </button>
          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="text-xs text-slate-400 hover:text-[#8B2121] transition-colors"
          >
            {showUrlInput ? "Hide URL input" : "Or paste URL instead"}
          </button>
        </div>
        {showUrlInput && (
          <div className="mt-2 flex gap-2">
            <input
              type="url"
              id={`url-input-${type}`}
              placeholder="https://..."
              className="flex-1 h-10 px-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById(`url-input-${type}`) as HTMLInputElement;
                if (input?.value) {
                  const tag = `<img src="${input.value}" alt="image" />`;
                  setForm((f) => ({ ...f, content: f.content + "\n" + tag }));
                  input.value = "";
                  setShowUrlInput(false);
                }
              }}
              className="px-3 py-2 rounded-xl text-xs font-bold text-white"
              style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
            >
              Insert
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid={`button-save-doc-${type}`}
          className="flex items-center gap-1.5 px-6 py-2.5 rounded-2xl text-white text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
          style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
        >
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : <><Save className="h-4 w-4" /> Save Documentation</>}
        </button>
      </div>
    </div>
  );
}

function DocAdminContent() {
  const [activeType, setActiveType] = useState("user-documentation");

  return (
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-[#8B2121]/10 dark:border-white/10 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Logo size="md" />
          <span className="text-xs font-bold text-[#8B2121] bg-[#8B2121]/10 px-2.5 py-1 rounded-full">Docs Admin</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 pb-16">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-5">Edit Documentation</h1>

        <div className="flex gap-2 mb-6">
          {DOC_TYPES.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setActiveType(type)}
              data-testid={`tab-doc-${type}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-bold border transition-colors ${
                activeType === type
                  ? "text-white border-[#8B2121]"
                  : "bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700"
              }`}
              style={activeType === type ? { background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" } : {}}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
          <DocEditor key={activeType} type={activeType} />
        </div>
      </div>
    </div>
  );
}

export default function DocAdmin() {
  return (
    <AdminLoginGate label="Docs Admin">
      <DocAdminContent />
    </AdminLoginGate>
  );
}
